import { useContext, useState } from 'react';
import useRakenneosaNavV2 from './services/useRakenneosaNavV2';
import RakenneosaNavView from './views/RakenneosaNavView';
import { ReportTemplateContext } from '../../ReportTemplate/ReportTemplateContext';
import { findLinkedProjectId } from '../../../../utils/ptsUtils';
import usePoranayteV2 from './services/usePoranayteV2';
import PoranayteView from './views/PoranayteView';
import ApiFeedbackBanner from './views/ApiFeedbackBanner';

// Poränäyte works as: pick/create a rakenneosa (its own modal, in
// RakenneosaNavView), then add any number of näytetunnus under it — "+
// Näyte" creates one immediately (usePoranayteV2.addSample auto-names it
// PN1/PN2/... when none is given) rather than opening a modal to ask for
// a name first; the tunnus is just an editable field in the table/cards
// like every other value, so there is nothing a name-first modal gave
// that renaming inline afterward doesn't already cover.
//
// Calls useRakenneosaNavV2 directly and renders RakenneosaNavView itself
// (every v2 block does this now — the old RakenneosaPickerV2 wrapper, which
// instantiated its own private copy of this hook and never exposed its
// optimistic nav back up, has been removed) so this block's own
// data-fetching (usePoranayteV2 below) keys off the SAME optimistic nav the
// tab/pill UI uses. Without that, a block reading nav from `data.nav` (the
// slow, debounced-save-gated prop) directly saw its tabs flip instantly but
// its actual table content/data kept lagging a full save round-trip behind.
export default function PoranayteBlockV2({ data, onChange, card, tableStartNumber }) {
  const id_kiinteisto = card?.id_kiinteisto ?? null;
  const templateCtx = useContext(ReportTemplateContext);
  const linkedProjectId = findLinkedProjectId(templateCtx?.sections ?? []);
  const activeSectionName = templateCtx?.activeSection?.name ?? null;
  const navSvc = useRakenneosaNavV2(id_kiinteisto, data?.nav || {}, next => onChange({ ...data, nav: next }), linkedProjectId, activeSectionName);
  const nav = navSvc.nav;
  const svc = usePoranayteV2(nav.rakId);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const pendingDeleteSample = svc.samples.find(s => s.id === pendingDeleteId);
  const confirmRemoveSample = () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    svc.removeSample(id);
  };

  return (
    <div style={{ maxWidth: 'none' }}>
      <RakenneosaNavView id_kiinteisto={id_kiinteisto} svc={navSvc} canCreateRakenneosa />
      <ApiFeedbackBanner feedback={svc.feedback} onClose={svc.clearFeedback} />
      {nav.rakId ? (
        <PoranayteView {...svc} removeSample={id => setPendingDeleteId(id)} taulukkoNum={tableStartNumber ?? 1} onAddSample={() => svc.addSample()} />
      ) : nav.tkId && navSvc.rakenneosat.length === 0 && (
        // Without this the table area just went blank when switching to a
        // tarkastuskohde with no rakenneosa yet — easy to misread as "stuck
        // on the previous tarkastuskohde's table" since nothing visibly
        // changed to explain why it's empty. Same message as the
        // standalone taloyhtiökortti workspace's equivalent empty state.
        <div style={{ padding: '18px 4px', textAlign: 'center', color: '#8e8e93', fontSize: '0.84rem' }}>
          Lisää ensin rakenneosa yllä nähdäksesi taulukon.
        </div>
      )}

      {pendingDeleteSample && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteId(null); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista näyte</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>
              Haluatko poistaa näytteen <strong>{pendingDeleteSample.nayte_tunnus || `#${pendingDeleteSample.id}`}</strong>?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteId(null)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={confirmRemoveSample}>Poista</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
